import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/vue-query";
import type { components } from "@scalty/contracts";

import { qk } from "../utils/queryKeys";
import type { IconName } from "../utils/icons";

type Notification = components["schemas"]["Notification"];
export type NotificationKind = components["schemas"]["NotificationKind"];
type NotificationList = components["schemas"]["NotificationList"];
type NotificationLastSeenEnvelope = components["schemas"]["NotificationLastSeenEnvelope"];
type SetNotificationLastSeenRequest = components["schemas"]["SetNotificationLastSeenRequest"];

export interface NotificationDescriptor {
  label: string;
  href: "/models" | "/datasets" | "/org/current/billing";
  destination: "Models" | "Datasets" | "Billing";
}

/**
 * DR-6's complete deterministic copy and navigation vocabulary.
 *
 * The destinations are the routes the portal actually has: documents and the
 * containers they are prepared in live under Datasets, and the organisation's
 * billing page is reached through `/org/current`, which the organisation shell
 * rewrites to the organisation's own slug. A row must land on a page.
 */
export const NOTIFICATION_DESCRIPTORS: Record<NotificationKind, NotificationDescriptor> = {
  training_complete: {
    label: "A training run finished",
    href: "/models",
    destination: "Models",
  },
  payment_failed: {
    label: "A payment couldn't be processed",
    href: "/org/current/billing",
    destination: "Billing",
  },
  sync_paused: {
    label: "A data sync was paused",
    href: "/datasets",
    destination: "Datasets",
  },
  credit_75: {
    label: "You've used 75% of your monthly credit",
    href: "/org/current/billing",
    destination: "Billing",
  },
  credit_100: {
    label: "You've reached your monthly credit limit",
    href: "/org/current/billing",
    destination: "Billing",
  },
  daily_spend: {
    label: "Daily spend alert: your configured threshold was exceeded",
    href: "/org/current/billing",
    destination: "Billing",
  },
};

/** The section icon each destination already wears in the sidebar. */
const DESTINATION_ICONS: Record<NotificationDescriptor["destination"], IconName> = {
  Models: "models",
  Datasets: "datasets",
  Billing: "billing",
};

/**
 * One notification as the bell's panel reads it. The contract carries `id`,
 * `kind`, `createdAt` and `unread` and nothing else — the sentence and the
 * destination come from the descriptor above, the time from the same formatter
 * the row's screen-reader label uses, and no state word is invented for a
 * notification that has none.
 */
export interface NotificationBellRow {
  id: string;
  label: string;
  facts: string;
  destination: string;
  to: string;
  icon: IconName;
  unread: boolean;
}

export function notificationBellRows(
  source: readonly PortalNotification[],
  now: Date = new Date(),
): NotificationBellRow[] {
  return source.map((row) => ({
    id: row.id,
    label: row.label,
    facts: formatNotificationTime(row.createdAt, now),
    destination: row.destination,
    to: row.href,
    icon: DESTINATION_ICONS[row.destination],
    unread: row.unread,
  }));
}

export interface PortalNotification extends Notification, NotificationDescriptor {
  group: "today" | "earlier";
}

export type NotificationFeedData = InfiniteData<NotificationList, string | null>;
type NotificationQueryKey = ReturnType<typeof qk.notifications>;
export type MarkAllStatus = "idle" | "pending" | "success" | "error";

export interface NotificationScope {
  userId: string;
  organizationId: string;
}

export interface MarkAllNotificationsRequest {
  scope: NotificationScope;
  cursor: string;
}

export interface NotificationLoadMoreGuard {
  hasNextPage: boolean;
  isFeedFetching: boolean;
  isMarkAllPending: boolean;
}

export interface NotificationMarkAllGuard {
  headCursor: string | null;
  hasUnread: boolean;
  isFeedFetching: boolean;
  isMarkAllPending: boolean;
}

export interface NotificationFeedActivity {
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isPaginationRequestStarting: boolean;
}

export const NOTIFICATION_PAGE_SIZE = 20;

/** Local-calendar grouping is intentional: DR-6 labels the viewer's Today. */
export function isSameLocalDay(iso: string, now: Date): boolean {
  const date = new Date(iso);
  return (
    date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate()
  );
}

function localDayDelta(earlier: Date, later: Date): number {
  const earlierDay = new Date(
    earlier.getFullYear(),
    earlier.getMonth(),
    earlier.getDate(),
  );
  const laterDay = new Date(later.getFullYear(), later.getMonth(), later.getDate());
  // Rounding keeps calendar-day labels correct across 23/25-hour DST days.
  return Math.round((laterDay.getTime() - earlierDay.getTime()) / 86_400_000);
}

/** Compact, honest DR-6 time label with an absolute fallback for older rows. */
export function formatNotificationTime(iso: string, now: Date = new Date()): string {
  const createdAt = new Date(iso);
  if (Number.isNaN(createdAt.getTime())) return "Unknown time";

  const dayDelta = localDayDelta(createdAt, now);
  if (dayDelta <= 0) {
    const diffMs = Math.max(0, now.getTime() - createdAt.getTime());
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} min ago`;
    return `${Math.floor(minutes / 60)} h ago`;
  }
  if (dayDelta === 1) return "Yesterday";
  if (dayDelta < 7) return `${dayDelta} days ago`;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    ...(createdAt.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  }).format(createdAt);
}

/** Expanded units keep the row's full screen-reader label natural to hear. */
export function formatNotificationAriaTime(iso: string, now: Date = new Date()): string {
  const compact = formatNotificationTime(iso, now);
  const minuteMatch = /^(\d+) min ago$/.exec(compact);
  if (minuteMatch) {
    const count = Number(minuteMatch[1]);
    return `${count} ${count === 1 ? "minute" : "minutes"} ago`;
  }
  const hourMatch = /^(\d+) h ago$/.exec(compact);
  if (hourMatch) {
    const count = Number(hourMatch[1]);
    return `${count} ${count === 1 ? "hour" : "hours"} ago`;
  }
  return compact;
}

/** Add presentation-only copy/grouping without changing server order or unread truth. */
export function presentNotifications(
  source: readonly Notification[],
  now: Date = new Date(),
): PortalNotification[] {
  return source.map((row) => ({
    ...row,
    ...NOTIFICATION_DESCRIPTORS[row.kind],
    group: isSameLocalDay(row.createdAt, now) ? "today" : "earlier",
  }));
}

export function notificationAriaLabel(
  notification: PortalNotification,
  now: Date = new Date(),
): string {
  const unread = notification.unread ? "Unread. " : "";
  return `${unread}${notification.label}. ${formatNotificationAriaTime(notification.createdAt, now)}. Opens ${notification.destination}.`;
}

/** The visible feed's read position always belongs to its first loaded page. */
export function visibleNotificationReadPosition(
  data: NotificationFeedData | undefined,
): Pick<NotificationList, "headCursor" | "lastSeenCursor"> {
  const firstPage = data?.pages[0];
  return {
    headCursor: firstPage?.headCursor ?? null,
    lastSeenCursor: firstPage?.lastSeenCursor ?? null,
  };
}

/** One serialization rule covers background refreshes and next-page fetches. */
export function canLoadMoreNotifications({
  hasNextPage,
  isFeedFetching,
  isMarkAllPending,
}: NotificationLoadMoreGuard): boolean {
  return hasNextPage && !isFeedFetching && !isMarkAllPending;
}

/** Mark-all starts only from stable visible server truth. */
export function canMarkAllNotificationsRead({
  headCursor,
  hasUnread,
  isFeedFetching,
  isMarkAllPending,
}: NotificationMarkAllGuard): boolean {
  return headCursor !== null && hasUnread && !isFeedFetching && !isMarkAllPending;
}

/** Normalize every query/pagination phase into the one production lock. */
export function isNotificationFeedBusy({
  isFetching,
  isFetchingNextPage,
  isPaginationRequestStarting,
}: NotificationFeedActivity): boolean {
  return isFetching || isFetchingNextPage || isPaginationRequestStarting;
}

/**
 * Apply a confirmed server read position to the infinite-query cache.
 * Rows clear only when the confirmed cursor equals the cache's current head;
 * if a newer head arrived concurrently, unread flags stay conservative until
 * the invalidation below refetches server truth.
 */
export function reconcileNotificationReadPosition(
  data: NotificationFeedData | undefined,
  confirmedLastSeenCursor: string,
): NotificationFeedData | undefined {
  if (!data) return data;
  const currentHead = visibleNotificationReadPosition(data).headCursor;
  const readThroughCurrentHead = currentHead === confirmedLastSeenCursor;

  return {
    ...data,
    pageParams: [...data.pageParams],
    pages: data.pages.map((page) => ({
      ...page,
      lastSeenCursor: confirmedLastSeenCursor,
      notifications: readThroughCurrentHead
        ? page.notifications.map((notification) => ({ ...notification, unread: false }))
        : page.notifications,
    })),
  };
}

/** Apply a mutation result only to the exact user/organization scope it captured. */
export function applyNotificationReadConfirmation(
  queryClient: QueryClient,
  request: MarkAllNotificationsRequest,
  confirmedLastSeenCursor: string,
): void {
  queryClient.setQueryData<NotificationFeedData>(
    qk.notifications(request.scope.userId, request.scope.organizationId),
    (current) => reconcileNotificationReadPosition(current, confirmedLastSeenCursor),
  );
}

function sameNotificationScope(
  left: NotificationScope | null | undefined,
  right: NotificationScope | null | undefined,
): boolean {
  return left !== null
    && left !== undefined
    && right !== null
    && right !== undefined
    && left.userId === right.userId
    && left.organizationId === right.organizationId;
}

/** Live NOTIFY-1 server state for both TopBar bells and their panels. */
export function useNotifications() {
  const queryClient = useQueryClient();
  const session = useSession();
  const signedInUserId = computed(() => session.value.data?.user.id ?? null);
  const activeOrganizationId = computed(
    () => session.value.data?.session.activeOrganizationId ?? null,
  );
  const notificationScope = computed<NotificationScope | null>(() => {
    if (signedInUserId.value === null || activeOrganizationId.value === null) return null;
    return {
      userId: signedInUserId.value,
      organizationId: activeOrganizationId.value,
    };
  });
  const feedQueryKey = computed(() =>
    qk.notifications(signedInUserId.value, activeOrganizationId.value),
  );

  const feedQuery = useInfiniteQuery<
    NotificationList,
    Error,
    NotificationFeedData,
    NotificationQueryKey,
    string | null
  >({
    queryKey: feedQueryKey,
    enabled: computed(() => notificationScope.value !== null),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: String(NOTIFICATION_PAGE_SIZE) });
      if (pageParam !== null) params.set("cursor", pageParam);
      return apiFetch<NotificationList>(`/notifications?${params.toString()}`, { signal });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const pages = computed<NotificationList[]>(() => feedQuery.data.value?.pages ?? []);
  const visibleReadPosition = computed(() =>
    visibleNotificationReadPosition(feedQuery.data.value),
  );
  const notifications = computed<PortalNotification[]>(() =>
    presentNotifications(pages.value.flatMap((page) => page.notifications)),
  );
  const today = computed(() => notifications.value.filter((row) => row.group === "today"));
  const earlier = computed(() => notifications.value.filter((row) => row.group === "earlier"));
  const todayRows = computed(() => notificationBellRows(today.value));
  const earlierRows = computed(() => notificationBellRows(earlier.value));

  const headCursor = computed(() => visibleReadPosition.value.headCursor);
  const lastSeenCursor = computed(() => visibleReadPosition.value.lastSeenCursor);
  const hasUnread = computed(
    () => headCursor.value !== null && headCursor.value !== lastSeenCursor.value,
  );

  const isInitialPending = computed(
    () => feedQuery.data.value === undefined
      && (feedQuery.isPending.value || feedQuery.isFetching.value),
  );
  const initialError = computed<Error | null>(() =>
    feedQuery.data.value === undefined && feedQuery.isError.value
      ? feedQuery.error.value
      : null,
  );
  const loadMoreError = computed<Error | null>(() =>
    feedQuery.isFetchNextPageError.value && !feedQuery.isFetchingNextPage.value
      ? feedQuery.error.value
      : null,
  );
  const isEmpty = computed(
    () => feedQuery.data.value !== undefined && notifications.value.length === 0,
  );

  const retryRequest = shallowRef<MarkAllNotificationsRequest | null>(null);
  const activeMarkRequest = shallowRef<MarkAllNotificationsRequest | null>(null);
  const paginationRequestInFlight = ref(false);
  const markAllMutation = useMutation({
    mutationFn: (request: MarkAllNotificationsRequest) => {
      const body: SetNotificationLastSeenRequest = { cursor: request.cursor };
      return apiFetch<NotificationLastSeenEnvelope>("/notifications/last-seen", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    onSuccess: async ({ lastSeenCursor: confirmedCursor }, request) => {
      applyNotificationReadConfirmation(queryClient, request, confirmedCursor);
      await queryClient.invalidateQueries({
        queryKey: qk.notifications(request.scope.userId, request.scope.organizationId),
        exact: true,
        refetchType: "active",
      });
    },
  });

  // The local request flags close the same-tick gap before TanStack publishes
  // its reactive fetching/pending state. They also keep mutation invalidation
  // serialized with pagination through the end of the awaited refetch.
  const isMarkAllPending = computed(
    () => activeMarkRequest.value !== null || markAllMutation.isPending.value,
  );
  const isFeedFetching = computed(() => isNotificationFeedBusy({
    isFetching: feedQuery.isFetching.value,
    isFetchingNextPage: feedQuery.isFetchingNextPage.value,
    isPaginationRequestStarting: paginationRequestInFlight.value,
  }));
  const showMarkAll = computed(() => headCursor.value !== null && hasUnread.value);
  const canMarkAll = computed(() => canMarkAllNotificationsRead({
    headCursor: headCursor.value,
    hasUnread: hasUnread.value,
    isFeedFetching: isFeedFetching.value,
    isMarkAllPending: isMarkAllPending.value,
  }));
  const canLoadMore = computed(() => canLoadMoreNotifications({
    hasNextPage: feedQuery.hasNextPage.value,
    isFeedFetching: isFeedFetching.value,
    isMarkAllPending: isMarkAllPending.value,
  }));
  const retryBelongsToCurrentScope = computed(() =>
    sameNotificationScope(retryRequest.value?.scope, notificationScope.value),
  );
  const activeRequestBelongsToCurrentScope = computed(() =>
    sameNotificationScope(activeMarkRequest.value?.scope, notificationScope.value),
  );
  const canRetryMarkAll = computed(
    () => retryRequest.value !== null
      && retryBelongsToCurrentScope.value
      && !isFeedFetching.value
      && !isMarkAllPending.value,
  );
  const markAllStatus = computed<MarkAllStatus>(() => {
    if (isMarkAllPending.value && activeRequestBelongsToCurrentScope.value) return "pending";
    if (markAllMutation.isError.value && retryBelongsToCurrentScope.value) return "error";
    if (
      markAllMutation.isSuccess.value
      && retryBelongsToCurrentScope.value
      && !hasUnread.value
    ) return "success";
    return "idle";
  });
  const markAllError = computed<Error | null>(() =>
    retryBelongsToCurrentScope.value ? (markAllMutation.error.value ?? null) : null,
  );

  watch(
    [signedInUserId, activeOrganizationId],
    (_current, previous) => {
      const [previousUserId, previousOrganizationId] = previous;
      if (previousUserId !== null && previousOrganizationId !== null) {
        void queryClient.cancelQueries({
          queryKey: qk.notifications(previousUserId, previousOrganizationId),
          exact: true,
        });
      }
      // A failed request is retryable only inside the scope that created it.
      // An already-sent request may still settle, but its captured key keeps
      // that result isolated from the newly active scope.
      retryRequest.value = null;
      markAllMutation.reset();
    },
  );

  function submitMarkAll(request: MarkAllNotificationsRequest): void {
    activeMarkRequest.value = request;
    void markAllMutation.mutateAsync(request)
      .catch(() => undefined)
      .finally(() => {
        if (activeMarkRequest.value === request) activeMarkRequest.value = null;
      });
  }

  function markAllRead(): void {
    const scope = notificationScope.value;
    const cursor = headCursor.value;
    if (!canMarkAll.value || scope === null || cursor === null) return;
    const request: MarkAllNotificationsRequest = {
      scope: { ...scope },
      cursor,
    };
    retryRequest.value = request;
    submitMarkAll(request);
  }

  function retryMarkAll(): void {
    const request = retryRequest.value;
    if (!canRetryMarkAll.value || request === null) return;
    // Deliberately resend the failed request's cursor, even if a newer head has
    // appeared since the first attempt (DR-6 C).
    submitMarkAll(request);
  }

  function retryInitial(): void {
    if (isFeedFetching.value || isMarkAllPending.value) return;
    void feedQuery.refetch({ cancelRefetch: false });
  }

  function loadMore(): void {
    if (!canLoadMore.value) return;
    paginationRequestInFlight.value = true;
    void feedQuery.fetchNextPage({ cancelRefetch: false })
      .catch(() => undefined)
      .finally(() => {
        paginationRequestInFlight.value = false;
      });
  }

  /* One lock for both controls the panel offers: neither marking read nor loading a further
     page may start while the feed is already talking to the server. */
  const busy = computed(() => isFeedFetching.value || isMarkAllPending.value);

  return {
    notifications,
    today,
    earlier,
    todayRows,
    earlierRows,
    busy,
    headCursor,
    lastSeenCursor,
    hasUnread,
    isEmpty,
    isInitialPending,
    initialError,
    retryInitial,
    hasNextPage: feedQuery.hasNextPage,
    isFeedFetching,
    isLoadingMore: feedQuery.isFetchingNextPage,
    loadMoreError,
    loadMore,
    canLoadMore,
    showMarkAll,
    canMarkAll,
    canRetryMarkAll,
    markAllStatus,
    markAllError,
    markAllRead,
    retryMarkAll,
  };
}
